import React, { useEffect, useState } from 'react';
import { Modal, Button, Table, Space, Input, Form, Upload, Typography, message } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, CloseOutlined, CheckOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * 推荐位模板 - 预览弹窗
 */
export const RecommendTemplatePreviewDialog = ({ open, imageUrl, onCancel }) => (
    <Modal title="模板预览" open={open} onCancel={onCancel} width={700} footer={null} destroyOnClose>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
            <img src={imageUrl} alt="模板预览" style={{ maxWidth: '100%', borderRadius: 8 }} />
        </div>
    </Modal>
);

/**
 * 推荐位模板 - 新增/编辑弹窗
 */
export const RecommendTemplateFormDialog = ({ open, editData, onCancel, onSubmit }) => {
    const [form] = Form.useForm();
    const [image, setImage] = useState('');
    const isEdit = !!editData;

    useEffect(() => {
        if (!open) return;
        if (editData) {
            form.setFieldsValue({ code: editData.code, name: editData.name });
            setImage(editData.image || '');
        } else {
            form.resetFields();
            setImage('');
        }
    }, [open, editData, form]);

    const handleConfirm = async () => {
        const values = await form.validateFields();
        if (!image) { message.warning('请上传模板图片'); return; }
        onSubmit({ id: editData?.id, code: values.code, name: values.name, image });
    };

    const beforeUpload = (file) => {
        const reader = new FileReader();
        reader.onload = e => setImage(e.target.result);
        reader.readAsDataURL(file);
        return false;
    };

    return (
        <Modal
            title={isEdit ? '编辑模板' : '添加模板'}
            open={open}
            onCancel={onCancel}
            width={500}
            destroyOnClose
            maskClosable={false}
            footer={
                <Space>
                    <Button icon={<CloseOutlined />} onClick={onCancel}>取消</Button>
                    <Button type="primary" icon={<CheckOutlined />} onClick={handleConfirm}>确定</Button>
                </Space>
            }
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label="模板ID"
                    name="code"
                    rules={[
                        { required: true, message: '请输入模板ID' },
                        { pattern: /^[a-zA-Z0-9_]+$/, message: '仅支持字母、数字、下划线' },
                        { max: 100, message: '不超过100个字符' },
                    ]}
                >
                    <Input placeholder="例如：tpl_chunjie" disabled={isEdit} maxLength={100} showCount />
                </Form.Item>
                <Form.Item label="模板名称" name="name" rules={[{ required: true, message: '请输入模板名称' }]}>
                    <Input placeholder="例如：春节红包" maxLength={50} showCount />
                </Form.Item>
                <Form.Item label="模板图片" required>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Upload showUploadList={false} beforeUpload={beforeUpload} accept="image/*">
                            <Button icon={<PlusOutlined />}>上传图片</Button>
                        </Upload>
                        {image && <img src={image} alt="模板" style={{ width: 60, height: 100, objectFit: 'cover', borderRadius: 6, border: '1px solid #e4e7ed' }} />}
                    </div>
                </Form.Item>
            </Form>
        </Modal>
    );
};

/**
 * 推荐位模板 - 管理弹窗
 */
export const RecommendTemplateManageDialog = ({ open, templates, onCancel, onChange }) => {
    const [formOpen, setFormOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [preview, setPreview] = useState({ open: false, imageUrl: '' });

    const handleSubmit = (data) => {
        if (data.id) {
            onChange(templates.map(t => (t.id === data.id ? { ...t, name: data.name, image: data.image } : t)));
            message.success('修改成功');
        } else {
            onChange([...templates, { id: Date.now(), code: data.code, name: data.name, image: data.image }]);
            message.success('添加成功');
        }
        setFormOpen(false);
    };

    const handleDelete = (row) => {
        onChange(templates.filter(t => t.id !== row.id));
        message.success('删除成功');
    };

    const columns = [
        { title: '模板ID', dataIndex: 'code', key: 'code', width: 120, render: code => <Text code>{code}</Text> },
        {
            title: '预览', key: 'preview', width: 100, align: 'center',
            render: (_, r) => r.image
                ? <img src={r.image} alt="预览" style={{ width: 88, height: 66, objectFit: 'cover', borderRadius: 4, cursor: 'pointer', border: '1px solid #e4e7ed' }} onClick={() => setPreview({ open: true, imageUrl: r.image })} />
                : '-',
        },
        { title: '名称', dataIndex: 'name', key: 'name', width: 140 },
        {
            title: '操作', key: 'action', width: 190, align: 'center',
            render: (_, record) => (
                <Space size={8}>
                    <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setPreview({ open: true, imageUrl: record.image })}>预览</Button>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => { setEditData(record); setFormOpen(true); }}>编辑</Button>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>删除</Button>
                </Space>
            ),
        },
    ];

    return (
        <Modal title="管理模板" open={open} onCancel={onCancel} width={750} footer={<Button icon={<CloseOutlined />} onClick={onCancel}>关闭</Button>} destroyOnClose>
            <div style={{ marginBottom: 16 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditData(null); setFormOpen(true); }}>添加模板</Button>
            </div>

            <Table dataSource={templates} columns={columns} rowKey="id" pagination={false} size="small" />
            <div style={{ marginTop: 12, fontSize: 13, color: '#909399' }}>每个漏斗层级可选用不同模板</div>

            <RecommendTemplateFormDialog
                open={formOpen}
                editData={editData}
                onCancel={() => setFormOpen(false)}
                onSubmit={handleSubmit}
            />
            <RecommendTemplatePreviewDialog
                open={preview.open}
                imageUrl={preview.imageUrl}
                onCancel={() => setPreview({ open: false, imageUrl: '' })}
            />
        </Modal>
    );
};
